import type { Address } from "@redotech/account-common/address";
import type { IndustryCategory } from "@redotech/account-common/industry-category";
import { InstallSource } from "@redotech/account-common/install-source";
import type { TeamDealSizing } from "@redotech/account-common/team-deal-sizing";
import { type RenderedTeamUser } from "@redotech/account-common/user";
import type { Payer } from "@redotech/congo-enums/payer";
import { MarketingFlagKey } from "@redotech/feature-flags-keys-marketing/keys";
import { OmsFlagKey } from "@redotech/feature-flags-keys-oms/keys";
import { PlatformFlagKey } from "@redotech/feature-flags-keys-platform/keys";
import { ReclaimFlagKey } from "@redotech/feature-flags-keys-reclaim/keys";
import { ReturnsFlagKey } from "@redotech/feature-flags-keys-returns/keys";
import { ShippingFlagKey } from "@redotech/feature-flags-keys-shipping/keys";
import { ShopperAiFlagKey } from "@redotech/feature-flags-keys-shopper-ai/keys";
import type { Json } from "@redotech/json/json";
import { Currency } from "@redotech/money/currencies";
import type { AeoSettings } from "@redotech/redo-aeo-common/aeo-settings";
import { SupportAgentSkills } from "@redotech/redo-agents-cards/support-agents/skills";
import type { ContextualBillingSettings } from "@redotech/redo-billing-common/contextual-billing-settings";
import type { DimensionalPackagingSettings } from "@redotech/redo-billing-common/dimensional-packaging/dimensional-packaging-billing-types";
import {
  isStripePaymentMethod,
  type PaymentMethod,
} from "@redotech/redo-billing-common/payout";
import { RedoInternalProducts } from "@redotech/redo-billing-common/redo-internal-products";
import type { ShopifySubscriptionMetadata } from "@redotech/redo-billing-common/shopify-subscription-metadata";
import type { ShopperFacingBillingStatus } from "@redotech/redo-billing-common/shopper-facing-billing-status";
import type {
  BillingSettings,
  BillingTeam,
  ExchangeAgentBillingSettings,
} from "@redotech/redo-billing-common/team-billing";
import type { DateRange } from "@redotech/redo-common-model/date-range";
import { NonZeroValueExchangeStrategy } from "@redotech/redo-common-model/exchange";
import type { ConciergeSettings } from "@redotech/redo-concierge-settings-common/concierge-settings";
import type { EmailSignUpSettings } from "@redotech/redo-concierge-settings-common/email-sign-up-settings";
import type { BrandKit } from "@redotech/redo-content-builder-common/brand-kit";
import { ShippingRateTable } from "@redotech/redo-conversion-core-common/redo-in-shipping/shipping-rate-table";
import { CartOptimizationSettings } from "@redotech/redo-conversion-settings-common/cart-optimization-settings";
import { CheckoutOptimizationSettings } from "@redotech/redo-conversion-settings-common/checkout-optimization-settings";
import { CustomsAugmentationSettings } from "@redotech/redo-conversion-settings-common/customs-settings";
import { LandedCostSettings } from "@redotech/redo-conversion-settings-common/landed-cost-settings";
import { PredictiveConversionSettings } from "@redotech/redo-conversion-settings-common/predictive-conversion-settings";
import type { CustomerAccountsSettings } from "@redotech/redo-customer-accounts-common/customer-accounts-settings";
import { CustomerPortalVersion } from "@redotech/redo-customer-portal-common/customer-portal-version";
import type { EmailFooterSettings } from "@redotech/redo-customer-portal-common/email-footer-settings";
import type { PackagePickupVariant } from "@redotech/redo-customer-portal-common/package-pickup-variant";
import { AccountTabSection } from "@redotech/redo-customer-widget-common/account-tab";
import { PredefinedChipOptions } from "@redotech/redo-customer-widget-common/customer-widget";
import { CustomerWidgetSettings } from "@redotech/redo-customer-widget-common/customer-widget-settings";
import { QuickAction } from "@redotech/redo-customer-widget-common/quick-action";
import type { WidgetHours } from "@redotech/redo-customer-widget-common/widget-hours";
import { DisputesSettings } from "@redotech/redo-dispute-settings-common/disputes-settings";
import type { FulfillmentRegionsSettings } from "@redotech/redo-fulfillment-common/fulfillment-regions/settings";
import type { securitySettings } from "@redotech/redo-fulfillment-common/security-settings";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import type { ProductsSettings } from "@redotech/redo-fulfillment-settings/products";
import type { ReFulfillmentSettings } from "@redotech/redo-fulfillment-settings/re-fulfillment";
import type { SuppliesSettings } from "@redotech/redo-fulfillment-settings/supplies";
import type { ImsSettings } from "@redotech/redo-inventory-common/ims-settings";
import type { InventorySettings } from "@redotech/redo-inventory-common/inventory-settings";
import { MarketingTeam } from "@redotech/redo-marketing-core-common/marketing/marketing-team-zod";
import type { MarketingSettings } from "@redotech/redo-marketing-settings-common/marketing-settings";
import type { RecoverSettings } from "@redotech/redo-marketing-settings-common/recover-settings";
import type { RecommerceSettings } from "@redotech/redo-marketplace-common/recommerce-settings";
import type { OrderEditingSettings } from "@redotech/redo-order-editing-settings-common/order-editing-settings";
import type { IOrderTrackingSettings } from "@redotech/redo-order-tracking-settings-common/order-tracking-settings";
import type { ReturnTrackingSettings } from "@redotech/redo-order-tracking-settings-common/return-tracking";
import type { ShippingInsurance } from "@redotech/redo-package-protection-common/shipping-insurance";
import type { PackageProtectionSettings } from "@redotech/redo-package-protection-settings/settings";
import type { RecognizeSettings } from "@redotech/redo-recognize-common/recognize-settings";
import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { ExchangeOrderNameSettings } from "@redotech/redo-return-core-common/exchange";
import type { ExchangeGroup } from "@redotech/redo-return-core-common/exchange-group";
import {
  AdvancedExchanges,
  DiscountDistributionMethod,
  type ExcessExchangeValueOption,
  ExchangeFinancialStrategy,
  ExchangeOrderLineItemPriceSource,
  ExchangesSelection,
  ExchangeType,
  type InstantExchangeHoldKind,
  LineItemDetailOption,
  type MetafieldDisplayConfig,
  NonInstantExchangeOrderProvision,
  OutOfStockAdvanced,
  ShopSiteNavigationMode,
  VariantPickerType,
} from "@redotech/redo-return-core-common/exchange-settings";
import type {
  Bundle,
  BundleRules,
} from "@redotech/redo-return-core-common/return-flow";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import type { ShippingLocation } from "@redotech/redo-return-core-common/shipping-location";
import type { SendcloudReturnDocumentType } from "@redotech/redo-return-core-common/shipping/sendcloud-return-document-type";
import type { ShopAgainAction } from "@redotech/redo-return-core-common/shop-again-action";
import type { Coverage } from "@redotech/redo-return-coverage-common/coverage";
import type { Automation } from "@redotech/redo-return-settings-common/automation";
import type { ContextualSettings } from "@redotech/redo-return-settings-common/contextual-settings";
import type { LabelExpirationSettings } from "@redotech/redo-return-settings-common/label-expiration-settings";
import type { ReturnMethod } from "@redotech/redo-return-settings-common/packing-instruction-return-method";
import type { PerReturnBillingSettings } from "@redotech/redo-return-settings-common/per-return-billing";
import type { PosSettings } from "@redotech/redo-return-settings-common/pos-settings";
import type { redoManagedReturnsSettings } from "@redotech/redo-return-settings-common/redo-managed-returns-settings";
import type { ReturnItemDispositionSettings } from "@redotech/redo-return-settings-common/return-item-disposition";
import type { ReturnsSettings } from "@redotech/redo-return-settings-common/returns-settings";
import type { ReturnsSortingMode } from "@redotech/redo-return-settings-common/returns-sorting-mode";
import type { WithdrawalsSettings } from "@redotech/redo-return-settings-common/withdrawals";
import type { YofiSettings } from "@redotech/redo-return-settings-common/yofi-settings";
import type { ExchangeAgentMerchantSettings } from "@redotech/redo-return-settings-primitives-common/exchange-agent-merchant-settings";
import { ExtendedWarrantySettings } from "@redotech/redo-return-warranties-common/extended-warranty";
import { WarrantiesSettings } from "@redotech/redo-return-warranties-common/warranties";
import type { ReviewsSettings } from "@redotech/redo-reviews-common/reviews-settings";
import { ShopifyIntegration } from "@redotech/redo-shopify-common/shopify";
import { ShopifySubscription } from "@redotech/redo-shopify-common/shopify-subscription";
import type { ShopifyExtensionSettings } from "@redotech/redo-shopify-settings-common/shopify-extension-settings";
import type { ShopperAgentAdminSettings } from "@redotech/redo-shopper-agent-settings-common/shopper-agent-admin-settings";
import { InfoModal } from "@redotech/redo-storefront-common/info-modal";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import type { ModalTextAdjustments } from "@redotech/redo-storefront-common/modal-text-adjustments";
import type {
  StorefrontConfig,
  StorefrontPlatform,
} from "@redotech/redo-storefront-common/storefront-config";
import type { ContactFormSettings } from "@redotech/redo-support-settings-common/contact-form";
import type { CustomerSupportSettings } from "@redotech/redo-support-settings-common/customer-support";
import type { SupportSettings } from "@redotech/redo-support-settings-common/support-settings";
import type { TeamEventDerivedState } from "@redotech/redo-team-events-common/event-derived-state";
import { TnplSettings } from "@redotech/redo-tnpl-settings-common/tnpl-settings";
import * as z from "zod";
import type { LanguageResourceOverride } from "./localization/resource";
import type { LocalizationSettings } from "./localization/settings";
import { type StorefrontPublishStatusMap } from "./storefront-publish";
import type { TeamAnonymization } from "./team/anonymization";

export const DEFAULT_WITHDRAWAL_AUTO_CANCELLED_ORDER_TAGS = ["Redo Withdrawal"];

export interface GetTeam {
  featureFlags: {
    [ReclaimFlagKey.RECLAIM_IMPACT_DASHBOARD]?: boolean;
    [MarketingFlagKey.MARKETING_SEGMENT_EMAIL_DOMAIN_FILTER]?: boolean;
    [MarketingFlagKey.EMAIL_TABLE_BLOCK]?: boolean;
    [MarketingFlagKey.UPTICK_INTEGRATION]?: boolean;
    [ShippingFlagKey.FEDEX_INVOICE_AI_VERIFICATION]?: boolean;
    [PlatformFlagKey.MERCHANT_NOTIFICATIONS]?: boolean;
    [ShopperAiFlagKey.SHOPPER_AGENT_AGGREGATE_INSIGHT]?: boolean;
    [OmsFlagKey.TEMU_INTEGRATION]?: boolean;
    [MarketingFlagKey.DISCOUNT_UI]?: boolean;
    [MarketingFlagKey.SHOPPER_AGENT_MESSAGE_STEP]?: boolean;
    [MarketingFlagKey.EMAIL_SHOPPER_LOCALIZATION]?: boolean;
    [ReturnsFlagKey.INTERNATIONAL_RETURNS_READINESS]?: boolean;
    [OmsFlagKey.NEW_SHIPMENT_DETAILS]?: boolean;
    [ReturnsFlagKey.SHOPIFY_RETURNS_BILLING_ENFORCE]?: boolean;
  };
  roles: string[];
  _id: string;
  email: string;
  createdAt: string;
  firstName: string;
  lastName: string;
  name: string;
  updatedAt: string;
  images: string[];
  updatePermissionsLink?: string;
  platform: StorefrontPlatform;
  shopify?: ShopifyIntegration;
  team: Team;
}

export const upsellButtonDefaultText = "Add to cart";

// StorefrontPublishStatus, StorefrontProductKey, and StorefrontPublishStatusMap
// live in ./storefront-publish; re-exported here for existing importers.

export type GiftCardRecipientType = "recipient" | "customer" | "both";

export interface Team extends BillingTeam {
  accountId?: string | null;
  /** Which Shopify app first installed Redo; decides the Reclaim channel. */
  installSource?: InstallSource | null;
  sortingMode?: ReturnsSortingMode;
  ipAddress?: string; // UPS requires this to create ghost accounts
  fulfillmentService?: string;
  fulfillmentServices: string[];
  customer_email: string;
  publicAddress?: Address;
  damagedAddress?: Address;
  address?: Address;
  customInStoreAddresses?: Address[];
  automation: Automation;
  failed_payouts: number;
  shutdown_date: string;
  shopifyReturnsBillingRequiredSince?: string;
  shopifyReturnsBillingShutdownDate?: string;
  _id: string;
  email: string;
  industryCategory?: IndustryCategory;
  teamAnonymization?: TeamAnonymization;
  intercomId: string;
  lastOrderSync: string;
  orderBackfillWatermark: string;
  orderBackfillMin: string;
  commentsoldBackSyncOrders?: {
    stopAt: string;
    current: string;
    intervalSeconds: number;
  };
  commentsoldOrderSyncAt?: string;
  bigcommerceBackSyncOrders?: { stopAt: string; current: string };
  bigcommerceOrderSyncAt?: string;
  name: string;
  displayName?: string;
  notification_email: string;
  claim_notification_email: string;
  warranty_notification_email: string;
  receipt_email: string;
  returnLocationId?: string;
  settings: Settings;
  redoInternalProducts?: RedoInternalProducts;
  storeUrl: string;
  widget_slug: string;
  shopifyCoverageProductSource?: "store" | "supplier";
  storePassword: string;
  createdAt: string;
  updatedAt?: string;
  multistorefront?: StorefrontConfig[];

  /**
   * Populated by the team-events consumer post-processing. Structure
   * mirrors the event namespace — see TeamEventDerivedState in
   * ./team-events/event-derived-state for the authoritative shape.
   * Do not write to this field from product code.
   */
  eventDerivedState?: TeamEventDerivedState;

  /**
   * Periodically-refreshed measurement snapshots about this team,
   * populated by cron tasks in redo/cron. Cached view — never the
   * source of truth.
   */
  metricsSnapshot?: {
    onlineStoreOrderVolume?: {
      recordedAt: string;
      last30Days?: number;
      lastYear?: number;
    };
    totalOrderVolume?: {
      recordedAt: string;
      last30Days?: number;
      lastYear?: number;
    };
  };

  /**
   * Deal-sizing snapshot populated by the onboarding-data-pull Temporal
   * workflow. Mirrors the `TeamDealSizing` Mongoose subdocument. Used by
   * the admin team-detail panel and the returns onboarding routing to
   * size coverage price and split self-serve vs human-handoff merchants.
   *
   * Cached view — never the source of truth. Re-run the workflow to refresh.
   */
  dealSizing?: TeamDealSizing;

  /** Hydrated from published CRDB. Missing/false means the agent is off. */
  shopperAgentEnabled?: boolean;
  publishedShopperAgent?: {
    enabled: boolean;
    iMessageConfig?: { enabled?: boolean | null } | null;
    rcsConfig?: { enabled?: boolean | null } | null;
  } | null;

  save(): Promise<void>;
}

const recommendationSettingsSchema_ = z.object({
  strategy: z.string(),
  version: z.number(),
  updatedAt: z.string(),
  primaryVendors: z.array(z.string()).nullish(),
});
export const recommendationSettingsSchema: z.ZodType<RecommendationSettings> =
  recommendationSettingsSchema_;
export interface RecommendationSettings extends z.infer<
  typeof recommendationSettingsSchema_
> {}

export const closeShopifyReturnOptions = [
  "enabled",
  "greenReturnsOnly",
  "disabled",
] as const;
export type CloseShopifyReturnOption =
  (typeof closeShopifyReturnOptions)[number];

/** Default price per human ticket in cents for SIMPLE_HUMAN_USAGE billing */
export const DEFAULT_HUMAN_TICKET_PRICE_IN_CENTS = 20;

export interface Recycling {
  enabled?: boolean;
  recyclableProductsCollection?: { id: string; name: string };

  productId?: string;
  labelPayer: Payer;
  chargeMerchantPerRecycleRequest: boolean;
  perRecycleRequestFee: number;
  minRecyclingProductQuantity: number;
}

export interface Settings {
  /** Operator-set override for the team's store currency. */
  currencyOverride?: Currency;
  recycling?: Recycling;
  concierge: ConciergeSettings;
  cartOptimization?: CartOptimizationSettings;
  checkoutOptimization?: CheckoutOptimizationSettings;
  landedCost?: LandedCostSettings;
  customsAugmentation?: CustomsAugmentationSettings;
  predictiveConversion?: PredictiveConversionSettings;
  crossSellPopoutEnabled: boolean;
  forceSyncShopifyProducts?: boolean;
  tnpl?: TnplSettings;
  ims?: ImsSettings;
  aeo?: AeoSettings;
  dimensionalPackaging?: DimensionalPackagingSettings;
  locationBasedAutocheckRemoval?: { enabled: boolean } | null;
  cartToggleShadowRootSelector?: string | null;
  commentsold: {
    sendDropShipperEmail?: boolean;
    useShopifyStorefront: boolean;
    recProductExclusions?: {
      variantIds?: string[];
      parentIds?: string[];
      tags?: string[];
    };
  };
  products: ProductsSettings;
  discountDistributionMethod: DiscountDistributionMethod;
  /** This is an array for legacy reasons, but it should only ever have one element. */
  shopifySubscription?: ShopifySubscription[];
  shopifySubscriptionMetadata: ShopifySubscriptionMetadata;
  inventory: InventorySettings;
  labelExpiration: LabelExpirationSettings;
  chatFlow: Json;
  useShadowRootInCart?: boolean;
  cartShadowRootSelector?: string;
  checkboxFontSize?: number;
  checkboxSelector?: string;
  checkboxText?: string;
  claimFlow: Json;
  /** Hides products in the return portal so they can't be returned */
  portalExcludedProductTags?: string[];
  portalExcludedCollections?: string[];
  portalExcludedProductIds?: string[];
  checkboxTextAfterRedo?: string;
  countries: string[];
  countriesPackageProtection: string[];
  coverage: Coverage;
  subscriptionEnabled: boolean; //Coverage and Subscription are exclusive. Either coverage is enabled or subscription is enabled but not both.
  /**
   * This boolean represents whether we should sell returns coverage to items that are eligible for returns.
   */
  coverageEnabled: boolean;
  coverageExcludeCollections: string[];
  coverageExcludeCustomerTags: string[];
  coverageExcludeSources: string[];
  coverageExcludeTags: string[];
  coverageExcludeProductTags: string[];
  coverageExcludeDiscountCodes: string[];
  coverageExcludeProperties: string[];
  coverageExcludeCentEnding: string[];
  additionalCoverageWebSources?: string[];
  bulkReturnsEnabled?: boolean;

  labelIncludeReturnType?: boolean;
  loadAllAdvancedExchangeProducts?: boolean;
  packagePickupVariants?: PackagePickupVariant[];
  excludeCoverageIfAnyInvalidProduct?: boolean;
  discountCodePartialMatch?: boolean;
  createdAt: string;
  customerSupport: CustomerSupportSettings;
  enabled: boolean;
  exchanges: ExchangeOrderNameSettings & {
    advancedExchanges: AdvancedExchanges;
    selection: ExchangesSelection;
    allowInstant: boolean;
    instantHoldAmount: InstantExchangeHoldKind;
    instantExpirationDays: number;
    notes: {
      disabled: boolean;
      includeDate: boolean;
      includeExistingNote: boolean;
      addNoteToOriginalOrders: boolean;
    };
    shopSite: { navigationMode: ShopSiteNavigationMode };
    outOfStockPolicy: RefundType;
    excessExchangeValue: ExcessExchangeValueOption;
    leftOverValueShopperSelectionIncentive?: {
      incentivizedMethod: RefundType;
      value: number;
      currencyOverrides: Partial<Record<Currency, number>>;
      perCurrencyAmountsEnabled: boolean;
    };
    outOfStockAdvanced: OutOfStockAdvanced;
    excludeProductTags: string[];
    excludeProductSkus: string[];
    recreateExpiredDiscounts: boolean;
    recreateAutomaticDiscountsAsCodes: boolean;
    blockDiscountsOnExchanges: boolean;
    shopSiteURL: string;
    shopSiteParams: string;
    shopSiteDomain: string;
    shopSitePath: string;
    enableOnHoldOrders: boolean;
    onHoldOrdersOnUpsellOnly?: boolean;
    exchangeShippingName?: string;
    differentPricedVariantExchanges: boolean;
    excludeDifferentPriceVariants?: boolean;
    variantExchangeProductTags: string[];
    usePreDiscountPrice: boolean;
    instantExchangeOnly?: boolean;
    shopSiteUseCartDiscounts?: boolean;
    reopenExpiredInstantUponShipment?: boolean;
    instantExchangeAutoRefundIfExpired: boolean;
    createLabelOnDraftOrderComplete: boolean;
    exchangeOptionsPreviewShowOriginalPrice?: boolean;
    nonZeroValueExchange?: boolean;
    nonZeroValueExchangeStrategy?: NonZeroValueExchangeStrategy;
    exchangeFinancialStrategy?: ExchangeFinancialStrategy;
    exchangeOrderLineItemPriceSource?: ExchangeOrderLineItemPriceSource;
    addTaxToExchangeOrders?: boolean;
    applyExchangeOrderPaymentGateways?: {
      enabled: boolean;
      upsellGateway: string;
      returnValueGateway: string;
    };
    excludedItemProperties?: { name: string; value: string }[];
    defaultPosRestock?: boolean;
    applyOriginalOrderDiscounts: ExchangeType[];
    applyAutomaticDiscounts: ExchangeType[];
    allowCustomerDiscountCodes: boolean;
    hideOutOfStockVariants?: boolean;
    hideUnpublishedVariants?: boolean;
    exchangeShippingRateTables?: ShippingRateTable[];
    /**
     * When true, the customer-selected exchange shipping rate is always
     * charged via Stripe, even on teams that bill the rest of the exchange
     * via a Shopify draft order. When false or unset, the exchange shipping
     * fee follows the team's exchange billing strategy (Stripe vs draft order).
     *
     * Not exposed in the merchant UI — used as a backwards-compatibility
     * escape hatch for teams that depend on the legacy always-Stripe behavior.
     */
    forceExchangeStripeBilling?: boolean;
    exchangeOrderCreationArtifactId?: string;
    addAdditionalExchangeTags?: boolean;
    shouldRemoveHoldTagWhenRelease?: boolean;
    /** @deprecated Unread since PR #44482 removed the v1 variant picker; every team gets manual-select. */
    variantPickerType: VariantPickerType;
    variantPickerColorHexMetafield?: string;
    nonInstantExchangeOrderProvision: NonInstantExchangeOrderProvision;
    separateQuantityForExchangeProducts?: boolean;
    applyFeesAsLineItem?: boolean;
    applyFeesAsLineItemName?: string;
    reapplyDiscountAmount?: boolean;
    reapplyCompareAtDiscount?: boolean;
    shopifyDeferReturnItemProcessing?: boolean;
    fulfillmentRegions?: FulfillmentRegionsSettings;
  };
  failed_payouts: number;
  hideCheckboxBranding: boolean;
  hideCheckboxPrice: boolean;
  hidePortalBranding: boolean;
  hidePoweredByInPortal?: boolean;
  hideCheckbox: boolean;
  images: string[];
  locations: ShippingLocation[];
  returnInStoreEnabled?: boolean;
  returnInStoreRadiusMiles?: number;
  inStoreDropOffPackingSlipEnabled?: boolean;
  merchantCoverage: Coverage;
  returnAdjustment: { positiveName: string; negativeName: string };
  policyType: string;
  productName?: string;
  protectionExcludeTags?: string[];
  receipt_email: string;
  redoAutoCheck: boolean;
  packageProtectionAutoCheck: boolean;
  returnFlow: Json;
  finalizeReturnFlow: Json;
  finalizeClaimFlow: Json;
  warrantyFlow: Json;
  returnItemDisposition: ReturnItemDispositionSettings;
  returnsUI?: {
    defaultCollapsed?: boolean;
    aiReviewDefaultCollapsed?: boolean;
    lineItemDetailOptions?: LineItemDetailOption[];
    productMetafieldDisplays?: MetafieldDisplayConfig[];
    variantMetafieldDisplays?: MetafieldDisplayConfig[];
    exposeExternalId?: boolean;
    useNewProcessingExperience?: boolean;
  };
  returnLocationId: string;
  returnPortal: string;
  returnPortalHeaderText?: string;
  returnPortalMissingOrderHeaderText?: string;
  returnPortalButtonText?: string;
  returnPortalGiftButtonText?: string;
  emailLoginLabelText?: string;
  orderNumberLoginLabelText?: string;
  loginSubtitleText?: string;
  registrationPortalTitle?: string;
  shippingCarrierOverride: string[];
  shippingCarrierAccounts?: string[];
  shippingInsurance: ShippingInsurance;
  shippingServiceOverride: Map<string, string[]>;
  generateQrCodes: boolean;
  /** When enabled, FedEx QR codes are generated for merchant-created labels. Requires FedEx QR code support to be enabled with EasyPost. */
  fedexQrCodesEnabledForMerchantRates?: boolean;
  /** When enabled, only carriers that support QR codes are used. In customer communications, the label is hidden unless QR code generation failed. */
  qrCodeOnlyShipping?: boolean;
  /** Restricts Sendcloud rates to QR-only or label-only options. Defaults to both. */
  sendcloudReturnDocumentType?: SendcloudReturnDocumentType;
  /** When true, merchants see all carrier rates in the create/edit label modal, ignoring their carrier override settings */
  ignoreTeamCarrierOverrideInMerchantDash?: boolean;
  shopAgainAction: ShopAgainAction;
  /** Shopify product ID for gift card orders (required when shopAgainAction is "gift_card_order") */
  giftCardOrderProductId?: string;
  riseTrigger: { url: string } | null;
  sku?: string;
  sku2?: string;
  returnsSKU?: string;
  packageProtectionSKU?: string;
  combinedSKU?: string;
  uniqueVariantSKU: boolean;
  indexBasedUniqueVariantStartingAt?: number;
  html?: string;
  updatedAt: string;
  upsert: { enabled: boolean; value: string; type: string };
  widget: { url: string };
  packingSlipEnabled?: boolean;
  includeExchangeItemsOnPackingSlip?: boolean;
  manualLabelPackingSlipEnabled?: boolean;
  customerLabelUpchargeFee?: number;
  dontChargeCustomerLabelUpchargeWhenOnlyOption?: boolean;
  excludeCustomerLabelFeeForExchanges?: boolean;
  includeMultipleCarrierOptions?: boolean;
  deductLabelFromCredit?: boolean;
  deductLabelFromRefund?: boolean;
  deductLabelFromExchange?: boolean;
  cartToggleEnabled: boolean;
  cartToggleSelector?: string;
  cartClickListenerSelector?: string;
  security?: securitySettings;
  toggleOnColor: string;
  toggleOffColor: string;
  toggleCircleColor: string;
  cartTogglePlacement?: InsertionPosition;
  cartReplaceItems?: boolean;
  packingInstructions?: { [key in ReturnMethod]: string[] };
  exchangeGroups: ExchangeGroup[];
  shopifyExtension?: ShopifyExtensionSettings;
  returns?: ReturnsSettings;
  withdrawals?: WithdrawalsSettings;

  pos?: PosSettings;
  analytics?: { lastScheduledReturnInsightDate?: string };
  /** allows merchant to change return address before return is approved */
  canEditReturnAddress?: boolean;
  packageProtection?: PackageProtectionSettings;
  /**
   * Per-product control over when each product is rendered on the storefront.
   * Layers on top of each product's `enabled` flag. Absent keys default to
   * {@link StorefrontPublishStatus.LIVE_SITE}.
   */
  storefrontPublishStatus?: StorefrontPublishStatusMap;
  /** HSA/FSA payments. Full shape lives on the backend Team schema. */
  hsa?: { enabled?: boolean };
  support?: SupportSettings;
  customerAccounts?: CustomerAccountsSettings;
  customerWidget?: CustomerWidgetSettings;
  notifyCustomerDefault: boolean;
  merchantPaidEnabledDates?: DateRange[];
  showLabelExpirationDate: boolean;
  modalTextAdjustments: ModalTextAdjustments;
  infoModal?: InfoModal;
  returnBarcodeWidth?: number;
  returnBarcodeHeight?: number;
  modalLogo?: string;
  defaultLineItemOunces?: number;
  toggleFontSize?: number;
  checkoutSelector?: string;
  exchangeBannerColor: string;
  exchangeBannerText: string;
  exchangeBannerFont: string;
  exchangeBannerFontColor: string;
  exchangeBannerShowDoneButton?: boolean;
  exchangeBannerCustomCss?: string;
  submitButtonSelector?: string;
  billingSettings: BillingSettings;
  bundleRulesList: BundleRules[];
  bundles: Bundle[];
  expandedBundlesIgnoreArchived?: boolean;
  pickupEnabled: boolean;
  pickupEnabledForHeavyItems?: boolean;
  pickupMaxLabelUpgradeRange?: number;
  pickupMaxCoveredAmountEnabled?: boolean;
  pickupMaxCoveredAmount?: number;
  shopifyPixelId?: string;
  splitPixelEnabled?: boolean;
  createShopifyReturns?: boolean;
  createShopifyReturnTypes: CompensationMethod[];
  createShopifyReturnsForClaimsAndWarranties?: boolean;
  addTrackingToShopifyReturns?: boolean;
  archiveShopifyOrdersAfterReturn?: boolean;
  closeShopifyReturnWhenProcessed?: CloseShopifyReturnOption;
  instantRefundEnabled?: boolean;
  syncBlockingPrefixes?: string;
  blockFulfillmentOrderPrefixes?: string;
  publicapi?: {
    discountsAreFinalSales?: boolean;
    coverageExcludeLocalPickup?: boolean;
    couponedItemsDiscountThreshold?: number;
  };
  sendgrid?: { enabled: boolean; fromEmail?: string; fromName?: string };
  emailFooter?: EmailFooterSettings;
  orderTracking?: IOrderTrackingSettings;
  orderEditing?: OrderEditingSettings;
  returnTracking?: ReturnTrackingSettings;
  // This should be renamed to exchangeAgentBilling, but we need to backfill existing teams first
  returnsAiExchange: ExchangeAgentBillingSettings;
  exchangeAgent: ExchangeAgentMerchantSettings;
  reviews?: ReviewsSettings;
  outboundLabels: OutboundLabelSettings;
  // Supplies storefront settings. Billing reuses the shared funding-source list
  // under outboundLabels.billing.fundingSources but keeps its own selection.
  supplies?: SuppliesSettings;
  loyaltyEnabled?: boolean;
  recommendations?: RecommendationSettings;
  exchangeRecommendations?: RecommendationSettings;
  marketing?: MarketingSettings;
  shopperAgentAdmin?: ShopperAgentAdminSettings;
  contextual?: ContextualSettings;
  contextualBilling?: ContextualBillingSettings;
  recover?: RecoverSettings;
  checkoutDeliveryEstimates?: {
    enabled?: boolean;
    suppressCartItemFields?: string[];
    suppressConfirmationSelectors?: string[];
  };
  variantExchangeTitle?: string;
  variantExchangeSubtitle?: string;
  exchangeGroupsExchangeTitle?: string;
  exchangeGroupsExchangeSubtitle?: string;
  sameItemTitle?: string;
  sameItemSubtitle?: string;
  newItemTitle?: string;
  preProtectedOrdersEnabled: boolean;
  preProtectedOrderMinutes?: number;
  _id: string;
  resourceOverride?: LanguageResourceOverride;
  customerPortalVersion?: CustomerPortalVersion;
  enableNonShopifyClaims?: boolean;
  nonShopifyClaimsCollection?: { id: string; name: string };
  nonShopifyClaimsDisableEditQuantity?: boolean;
  /** @deprecated Use settings.recycling.enabled instead */
  convertRecyclingProducts?: boolean;
  /** @deprecated Use settings.recycling.productId instead */
  recyclingProductId?: string;
  recognize?: RecognizeSettings;
  recommerce?: RecommerceSettings;
  hideManualReviewStep?: boolean;
  hideSummaryCard?: boolean;
  hideGiftOption?: boolean;
  disableRefundLineItems: boolean;
  warranties: WarrantiesSettings;
  extendedWarranties?: ExtendedWarrantySettings;
  brandKit: BrandKit;
  /** @deprecated  Use autoProcessGreenReturnTypes instead */
  autoProcessGreenReturns: boolean;
  autoProcessGreenReturnTypes?: ReturnTypeEnum[];
  autoProcessMerchantCreatedGreenReturnTypes?: ReturnTypeEnum[];
  // Returns analog of exchanges.notes.addNoteToOriginalOrders — stored flat here because
  // returns settings don't have a nested notes namespace like exchanges does.
  addOrderNotes?: boolean;
  hideFeeInCompensationMethodModal?: boolean;
  email?: { utm?: { campaign?: { prefix?: string } } };
  createMultipleShopifyReturns?: boolean;
  createSeparateShopifyReturnForGreenReturns?: boolean;
  yofi?: YofiSettings;
  perReturnBilling?: PerReturnBillingSettings;
  redoManagedReturns?: redoManagedReturnsSettings;
  reFulfillment?: ReFulfillmentSettings;
  migratedFromTeamIds?: string[];
  localization?: LocalizationSettings;
  disputes?: DisputesSettings;
}

export type RenderedTeam = Omit<Team, "users"> & { users: RenderedTeamUser[] };

export type RefundType = "store_credit" | "refund";

export interface TeamInfo {
  id: string;
  widgetSlug: string;
  returnsEnabled: boolean;
  customerClaimsEnabled: boolean;
  orderTrackingEnabled: boolean;
  marketingEnabled: boolean;
  marketingSubscribeFromLogin: boolean;
  customerAccountsEnabled: boolean;
  conciergeEnabled: boolean;
  conciergeLiveChat: boolean;
  conciergeSettings: {
    general: { assistantName: string | undefined; welcomeMessage: string };
    emailSignUp?: EmailSignUpSettings | undefined;
    proactiveChipEngagement: boolean;
    ticketHandoffSettings?: {
      handoffType?: string;
      emailHandoffMessage?: string;
      forwardToExternalEmail?: boolean;
      collectCustomerEmailBeforeHandoff?: boolean;
    };
    skills?: SupportAgentSkills;
    useOmsEventsForOrderEditing?: boolean;
    tryItOnMessage?: string;
  };
  chips: {
    selectedPredefinedChips: PredefinedChipOptions[];
    customChips: string[];
  };
  accountTab: {
    registerHeader: string;
    registerSubtext: string;
    sections: AccountTabSection[];
  };
  showAccountsPage?: boolean;
  overrideAccountLinks: boolean;
  redirectFromAccountsPages: boolean;
  hideButton: boolean;
  multipassAvailable: boolean;
  supportFeaturesEnabled: boolean;
  url: string;
  chatHeadingLogoUrl: string;
  storeUrl: string;
  customerPortalDomain: string;
  customerPortalPathPrefix: string;
  storefrontAccessToken: string;
  redoCoverageProductIds?: string[];
  customerPortalButtonText?: string;
  returnButtonText: string;
  claimButtonText: string;
  messagingHeadingText: string;
  mainHeadingText: string;
  homeHeadingText: string;
  contactFormSettings?: ContactFormSettings;
  name: string;
  privacyPolicyUrl?: string;
  termsAndConditionsUrl?: string;
  chatFlow: Json;
  widgetCustomCss?: string;
  widgetQuickActions?: QuickAction[];
  widgetQuickActionFontSize?: string;
  csat: { enabled: boolean; surveyMethods: string[]; allowRemarks: boolean };
  selfServe: {
    startReturnEnabled: boolean;
    startReturnLabel: string;
    startClaimEnabled: boolean;
    editOrderEnabled: boolean;
    editOrderLabel: string;
    trackPackageEnabled: boolean;
    trackPackageSettingLabel: string;
  };
  hideWidgetAfterHours?: boolean;
  hideWidgetOnProductPages?: boolean;
  widgetPrimaryColor?: string;
  widgetSecondaryColorEnabled?: boolean;
  widgetSecondaryColor?: string;
  widgetFontFamily?: string;
  widgetMessageBubbleFontSize?: string;
  widgetHeaderFontSize?: string;
  widgetButtonFontSize?: string;
  chatWithUsButtonText?: string;
  widgetPosition?: "left" | "right";
  xAdjustment?: string;
  yAdjustment?: string;
  mobileXAdjustment?: string;
  mobileYAdjustment?: string;
  zIndex?: number;
  mobileZIndex?: number;
  bubbleButtonText?: string;
  gradientSetting?: { gradientStrength?: number; inverted?: boolean };
  widgetHoursTimezone?: string;
  widgetHoursEnabled?: boolean;
  widgetHours?: WidgetHours | undefined;
  openToWelcomePage?: boolean;
  mobileFullscreen?: boolean;
}

export class OrderTrackingSettings {
  constructor(
    private readonly teamSettings: Pick<
      MarketingTeam["settings"],
      "orderTracking" | "shopifySubscription" | "marketing"
    >,
  ) {}

  servicesActive() {
    return !!(
      this.teamSettings?.orderTracking?.enabled &&
      this.teamSettings.orderTracking.billing?.invoiceBilling?.enabled
    );
  }

  canSendTexts() {
    return this.teamSettings?.orderTracking?.smsEnabled;
  }
}

interface TeamCanUseAiInput {
  settings?: {
    support?: {
      ai?: {
        enabled?: boolean | null;
        autopilotEnabled?: boolean | null;
      } | null;
      billing?: {
        ai?: {
          billingAccepted?: boolean | null;
          atShopifyCap?: boolean | null;
          lastDayActive?: string | Date | null;
          freeTrialEndDate?: string | Date | null;
        } | null;
      } | null;
    } | null;
  } | null;
  shopperAgentEnabled?: boolean;
}

export const teamCanUseAi = (team?: TeamCanUseAiInput): boolean => {
  if (team?.shopperAgentEnabled) {
    return true;
  }

  const ai = team?.settings?.support?.ai;
  const billing = team?.settings?.support?.billing?.ai;
  if (ai && billing) {
    const billingConfirmed = billing.billingAccepted;
    const atShopifyCap = billing.atShopifyCap;
    const lastDayActive = billing.lastDayActive;
    const lastDayActiveIsInFuture = lastDayActive
      ? new Date(lastDayActive) > new Date()
      : false;
    const freeTrialEndDate = billing.freeTrialEndDate;
    const inFreeTrial = freeTrialEndDate
      ? new Date(freeTrialEndDate) > new Date()
      : false;

    return (
      Boolean(ai.enabled) &&
      (Boolean(billingConfirmed) || lastDayActiveIsInFuture || inFreeTrial) &&
      !atShopifyCap
    );
  }
  return false;
};

export const allowSupportAiCopilot = (team?: Team): boolean => {
  return team?.settings?.support?.ai?.copilotEnabled ?? true;
};

export const shouldAutoGenerateAiResponses = (team?: Team): boolean => {
  return team?.settings?.support?.ai?.autoGenerateResponses ?? false;
};

export const shouldUseTeamEmailSignature = (team?: Team): boolean => {
  return team?.settings?.support?.ai?.useEmailSignature ?? false;
};

export const allowSupportAutopilot = (team?: TeamCanUseAiInput): boolean => {
  const allowed = teamCanUseAi(team);
  return (
    (allowed &&
      (team?.settings?.support?.ai?.autopilotEnabled ||
        team?.shopperAgentEnabled)) ??
    false
  );
};

export function isSupportFeaturesEnabled({
  isSupportEnabled,
  hideSupportWidgetFeatures,
}: {
  isSupportEnabled: boolean;
  hideSupportWidgetFeatures: boolean;
}): boolean {
  return isSupportEnabled && !hideSupportWidgetFeatures;
}

export function isConciergeLiveChatEnabled({
  isConciergeEnabled,
  automateLiveChat,
}: {
  isConciergeEnabled: boolean;
  automateLiveChat: boolean;
}): boolean {
  return isConciergeEnabled && automateLiveChat;
}

export function getChatHeadingLogoUrl({
  widgetHeadingLogoUrl,
  brandKitFaviconUrl,
  portalFaviconUrl,
}: {
  widgetHeadingLogoUrl: string | undefined;
  brandKitFaviconUrl: string | undefined;
  portalFaviconUrl: string | undefined;
}): string {
  return widgetHeadingLogoUrl || brandKitFaviconUrl || portalFaviconUrl || "";
}

export function isConciergeChatWithTeamMemberEnabled(
  supportFeaturesEnabled: boolean,
  isWithinHours: boolean,
): boolean {
  return supportFeaturesEnabled && isWithinHours;
}

export function shouldAutoProcessGreenReturnType({
  returnType,
  autoProcessGreenReturnTypes,
  autoProcessGreenReturns,
}: {
  returnType: ReturnTypeEnum;
  autoProcessGreenReturnTypes: ReturnTypeEnum[] | null | undefined;
  autoProcessGreenReturns: boolean | null | undefined;
}): boolean {
  return (
    autoProcessGreenReturnTypes?.includes(returnType) ||
    !!autoProcessGreenReturns
  );
}

export function shouldAutoProcessMerchantCreatedGreenReturnType({
  returnType,
  autoProcessMerchantCreatedGreenReturnTypes,
}: {
  returnType: ReturnTypeEnum;
  autoProcessMerchantCreatedGreenReturnTypes:
    | ReturnTypeEnum[]
    | null
    | undefined;
}): boolean {
  return !!autoProcessMerchantCreatedGreenReturnTypes?.includes(returnType);
}

// This function returns the first payment method that should be attempted.
// If there is no primary payment method, it will return a secondary one, if available.
export function teamGetPaymentMethod(
  stripe: Team["stripe"],
): PaymentMethod | null {
  if (isStripePaymentMethod(stripe?.paymentMethod)) {
    return stripe.paymentMethod;
  } else if (isStripePaymentMethod(stripe?.secondaryPaymentMethod)) {
    return stripe.secondaryPaymentMethod;
  } else if (stripe?.card?.id) {
    return { legacyCard: stripe.card };
  }
  return null;
}

/**
 * This function returns an alternate payment method, if available.
 * If there is no primary method, it is assumed that the secondary
 * method has already been tried and will return null.
 */
export function teamGetAlternatePaymentMethod(
  stripe: Team["stripe"],
): PaymentMethod | null {
  if (!isStripePaymentMethod(stripe?.paymentMethod)) {
    return null;
  }
  if (isStripePaymentMethod(stripe?.secondaryPaymentMethod)) {
    return stripe.secondaryPaymentMethod;
  }
  if (stripe?.card?.id) {
    return { legacyCard: stripe.card };
  }
  return null;
}

export interface ShopperFacingBillingInfo {
  status: ShopperFacingBillingStatus;
}
